import React, { useState, useEffect } from 'react';

interface SecurityDashboardProps {
  token: string;
}

const CARD = { background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '16px' };

const SecurityDashboard: React.FC<SecurityDashboardProps> = ({ token }) => {
  const [firewall, setFirewall] = useState<any>({});
  const [connections, setConnections] = useState<any[]>([]);
  const [connTotal, setConnTotal] = useState(0);
  const [bans, setBans] = useState<any>({});
  const [visitors, setVisitors] = useState<any[]>([]);
  const [visitorTotal, setVisitorTotal] = useState(0);
  const [banIp, setBanIp] = useState('');
  const [banMsg, setBanMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshRate, setRefreshRate] = useState(0); // 0 = off, 1/5/10 = seconds
  const [connFilter, setConnFilter] = useState('all'); // 'all', 'external', 'internal'

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [f, c, b, v] = await // eslint-disable-next-line
      Promise.all([
        fetch('/api/admin/security/firewall', auth).then(r => r.ok ? r.json() : {}),
        fetch('/api/admin/security/connections', auth).then(r => r.ok ? r.json() : { connections: [], total: 0 }),
        fetch('/api/admin/security/bans', auth).then(r => r.ok ? r.json() : {}),
        fetch('/api/admin/security/visitors', auth).then(r => r.ok ? r.json() : { visitors: [], total: 0 })
      ]);
      setFirewall(f);
      setConnections(c.connections || []);
      setConnTotal(c.total || 0);
      setBans(b);
      setVisitors(v.visitors || []);
      setVisitorTotal(v.total || 0);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  // Auto-refresh connections
  useEffect(() => {
    if (!refreshRate) return;
    const id = setInterval(() => {
      fetch('/api/admin/security/connections', auth)
        .then(r => r.ok ? r.json() : { connections: [], total: 0 })
        .then(c => { setConnections(c.connections || []); setConnTotal(c.total || 0); })
        .catch(() => {});
    }, refreshRate * 1000);
    return () => clearInterval(id);
  }, [refreshRate]);

  const handleBan = async () => {
    if (!banIp.trim()) return;
    setBanMsg('');
    try {
      const r = await fetch('/api/admin/security/ban', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ip: banIp.trim(), reason: 'Manual ban' })
      });
      const d = await r.json();
      setBanMsg(d.success ? `✅ Banned ${banIp}` : `❌ ${d.message || d.error}`);
      setBanIp('');
      loadAll();
    } catch { setBanMsg('❌ Error'); }
    setTimeout(() => setBanMsg(''), 5000);
  };

  const handleUnban = async (ip: string) => {
    try {
      await fetch('/api/admin/security/unban', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ip })
      });
      setBanMsg(`✅ Unbanned ${ip}`);
      loadAll();
    } catch {}
    setTimeout(() => setBanMsg(''), 5000);
  };

  return (
    <div>
      <h2 style={{ margin: '0 0 20px 0', fontSize: '20px', color: '#0f172a' }}>Security Dashboard</h2>

      {/* Status badges */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ padding: '10px 16px', borderRadius: '8px', background: firewall.active ? '#d1fae5' : '#fee2e2', color: firewall.active ? '#065f46' : '#991b1b', fontSize: '13px', fontWeight: 600 }}>
          {firewall.active ? '🛡️ Firewall Active' : '⚠️ Firewall Inactive'}
        </div>
        <div style={{ padding: '10px 16px', borderRadius: '8px', background: '#f0f9ff', color: '#0369a1', fontSize: '13px', fontWeight: 600 }}>
          🔗 {connTotal} Active Connections
        </div>
      </div>

      {/* Tabs */}
      <div style={{ background: 'white', borderBottom: '1px solid #e2e8f0', padding: '0 16px', display: 'flex', gap: '20px', marginBottom: '20px', borderRadius: '8px 8px 0 0' }}>
        {['overview', 'connections', 'bans', 'block', 'visitors'].map(t => (
          <button key={t} onClick={() => setActiveTab(t)}
            style={{ padding: '10px 0', background: 'none', border: 'none', cursor: 'pointer', fontWeight: activeTab === t ? '600' : '400', borderBottom: activeTab === t ? '2px solid #533afd' : '2px solid transparent', color: activeTab === t ? '#533afd' : '#64748b', fontSize: '13px' }}>
            {t === 'overview' && '📊 Overview'}
            {t === 'connections' && '🔗 Connections'}
            {t === 'bans' && '🚫 Bans'}
            {t === 'block' && '⛔ Block IP'}
            {t === 'visitors' && '👥 Visitors'}
          </button>
        ))}
      </div>

      {banMsg && <div style={{ padding: '10px', borderRadius: '8px', marginBottom: '12px', background: banMsg.includes('✅') || banMsg.includes('❌') ? banMsg.includes('✅') ? '#d1fae5' : '#fee2e2' : '#fef3c7', color: banMsg.includes('✅') ? '#065f46' : banMsg.includes('❌') ? '#991b1b' : '#92400e', fontSize: '13px' }}>{banMsg}</div>}

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <div>
          <div style={CARD}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#0f172a' }}>🛡️ Firewall Rules</h3>
            <pre style={{ background: '#1e1e1e', color: '#d4d4d4', padding: '16px', borderRadius: '8px', overflow: 'auto', maxHeight: '300px', fontSize: '12px', lineHeight: 1.5 }}>{firewall.rules || 'Loading...'}</pre>
          </div>
          <div style={CARD}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#0f172a' }}>🚫 Fail2Ban Status</h3>
            <pre style={{ background: '#1e1e1e', color: '#d4d4d4', padding: '16px', borderRadius: '8px', overflow: 'auto', maxHeight: '200px', fontSize: '12px' }}>{bans.jails || 'Loading...'}</pre>
          </div>
          <button onClick={loadAll} disabled={loading} style={{ padding: '8px 16px', background: '#2a5298', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
            {loading ? 'Refreshing...' : '🔄 Refresh All'}
          </button>
        </div>
      )}

      {/* CONNECTIONS TAB */}
      {activeTab === 'connections' && (
        <div style={CARD}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <h3 style={{ margin: 0, fontSize: '15px', color: '#0f172a' }}>🔗 Active Connections ({connTotal})</h3>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: '#94a3b8' }}>Auto-refresh:</span>
              {[0, 1, 5, 10].map(s => (
                <button key={s} onClick={() => setRefreshRate(s)}
                  style={{ padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                    fontSize: '12px', fontWeight: refreshRate === s ? '600' : '400',
                    background: refreshRate === s ? '#533afd' : '#f1f5f9',
                    color: refreshRate === s ? 'white' : '#475569' }}>
                  {s === 0 ? 'Off' : `${s}s`}
                </button>
              ))}
              {refreshRate > 0 && <span style={{ fontSize: '11px', color: '#10b981', marginLeft: '4px' }}>● Live</span>}
            </div>
          </div>
          {connections.length === 0 ? (
            <p style={{ color: '#94a3b8', fontSize: '13px' }}>No active connections found.</p>
          ) : (
            <div>
              {/* Filter bar */}
              <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
                {[
                  { k: 'all', label: `All (${connections.length})` },
                  { k: 'external', label: `External (${connections.filter(c => { const ip = (c.remote||'').split(':')[0]; return ip !== '0.0.0.0' && ip !== '*' && !ip.startsWith('127.') && !ip.startsWith('172.') && !ip.startsWith('10.') && !ip.startsWith('192.168.') && !ip.match(/^::/); }).length})` },
                  { k: 'internal', label: `Internal (${connections.filter(c => { const ip = (c.remote||'').split(':')[0]; return ip === '0.0.0.0' || ip === '*' || ip.startsWith('127.') || ip.startsWith('172.') || ip.startsWith('10.') || ip.startsWith('192.168.') || ip.match(/^::/); }).length})` }
                ].map(f => (
                  <button key={f.k} onClick={() => setConnFilter(f.k)}
                    style={{ padding: '4px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                      fontSize: '12px', fontWeight: connFilter === f.k ? '600' : '400',
                      background: connFilter === f.k ? '#533afd' : '#f1f5f9',
                      color: connFilter === f.k ? 'white' : '#475569' }}>
                    {f.label}
                  </button>
                ))}
              </div>
              <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Proto</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Local</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Remote</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>State</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>PID</th>
                  </tr>
                </thead>
                <tbody>
                  {connections
                    .filter(c => {
                      if (connFilter === 'all') return true;
                      const ipRaw = (c.remote || '').split(':')[0];
                      const ip = ipRaw.replace(/^\[::ffff:/, '').replace(/^\[::/, '');
                      const isInternal = ip === '0.0.0.0' || ip === '*' || ip === '0' || ip.startsWith('127.') || ip.startsWith('172.') || ip.startsWith('10.') || ip.startsWith('192.168.');
                      return connFilter === 'external' ? !isInternal : isInternal;
                    })
                    .map((c, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '6px 8px' }}>{c.proto}</td>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '11px' }}>{c.local}</td>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '11px' }}>{c.remote}</td>
                      <td style={{ padding: '6px 8px' }}>
                        <span style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '11px', background: c.state === 'ESTAB' ? '#d1fae5' : '#f1f5f9', color: c.state === 'ESTAB' ? '#065f46' : '#475569' }}>{c.state}</span>
                      </td>
                      <td style={{ padding: '6px 8px', fontSize: '11px' }}>{c.pid}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </div>
            )}
        </div>
      )}

      {/* BANS TAB */}
      {activeTab === 'bans' && (
        <div>
          <div style={CARD}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#0f172a' }}>🚫 Banned IPs (Fail2Ban)</h3>
            <pre style={{ background: '#1e1e1e', color: '#d4d4d4', padding: '16px', borderRadius: '8px', overflow: 'auto', maxHeight: '200px', fontSize: '12px' }}>{bans.bannedIps || 'No recent bans'}</pre>
          </div>
          <div style={CARD}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#0f172a' }}>🚫 Fail2Ban SSHD Status</h3>
            <pre style={{ background: '#1e1e1e', color: '#d4d4d4', padding: '16px', borderRadius: '8px', overflow: 'auto', maxHeight: '200px', fontSize: '12px' }}>{bans.sshdStatus || 'No data'}</pre>
          </div>
          <div style={CARD}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#0f172a' }}>Fail2Ban Jails</h3>
            <pre style={{ background: '#1e1e1e', color: '#d4d4d4', padding: '16px', borderRadius: '8px', overflow: 'auto', maxHeight: '200px', fontSize: '12px' }}>{bans.jails || 'Loading...'}</pre>
          </div>
        </div>
      )}

      {/* BLOCK IP TAB */}

      {activeTab === 'visitors' && (
        <div style={CARD}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#0f172a' }}>{'\uD83D\uDC65'} Recent Visitors ({visitorTotal})</h3>
          {visitors.length === 0 ? (
            <p style={{ color: '#94a3b8', fontSize: '13px' }}>No recent visitor data.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>IP</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Path</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Status</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>User Agent</th>
                  </tr>
                </thead>
                <tbody>
                  {visitors.map((v, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '11px' }}>{v.ip}</td>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '11px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.path}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                        <span style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '11px',
                          background: v.status.startsWith('2') ? '#d1fae5' : v.status.startsWith('4') ? '#fef3c7' : v.status.startsWith('5') ? '#fee2e2' : '#f1f5f9',
                          color: v.status.startsWith('2') ? '#065f46' : v.status.startsWith('4') ? '#92400e' : v.status.startsWith('5') ? '#991b1b' : '#475569' }}>{v.status}</span>
                      </td>
                      <td style={{ padding: '6px 8px', fontSize: '10px', color: '#64748b', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.agent}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {activeTab === 'block' && (
        <div style={CARD}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#0f172a' }}>⛔ Block / Unblock IP</h3>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <input value={banIp} onChange={e => setBanIp(e.target.value)} placeholder="Enter IP address (e.g. 192.168.1.1)"
              style={{ flex: 1, minWidth: '250px', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }} />
            <button onClick={handleBan} style={{ padding: '10px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>
              🚫 Block IP
            </button>
          </div>
          {bans.bannedIps && bans.bannedIps.split('\n').filter((l: string) => l.includes('Ban')).map((line: string, i: number) => {
            const match = line.match(/(\d+\.\d+\.\d+\.\d+)/);
            const ip = match ? match[0] : '';
            return ip ? (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>{ip}</span>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>{line.substring(0, 30)}</span>
                <button onClick={() => handleUnban(ip)} style={{ padding: '4px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', color: '#065f46' }}>Unban</button>
              </div>
            ) : null;
          })}
        </div>
      )}
    </div>
  );
};

export default SecurityDashboard;